import type { FC } from 'react';
import { useContext, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import type { PapiUser } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import BirthdayCake from '@wix/da-ds/pkg/Icons/16x16/BirthdayCake';
import DialogBanner from '@wix/da-ds/pkg/DialogBanner';
import Flex from '@wix/da-ds/pkg/Flex';
import { actions } from '@wix/da-shared-react/pkg/Modals';
import { popModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';

import baseStyles from './BirthdayBanner.scss';

interface Props {
  user: PapiUser;
  className?: string;
}

const UserBirthdayBanner: FC<Props> = ({ className, user }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useContext(MobileContext);
  const dispatchPopModal = useAction(popModal);

  const showGiftCake = user.isBirthweek;

  const saleKey = isMobile
    ? 'watch.birthday.banner.textWithLink.mobile'
    : 'watch.birthday.banner.textWithLink';

  const { username } = user;

  const giveCakeBadge = useCallback(() => {
    dispatch(
      actions.pushBadgeModal(
        user.username,
        undefined,
        () => dispatchPopModal(),
        'cake'
      )
    );
  }, [dispatch, user.username, dispatchPopModal]);

  const giftCakeButton = showGiftCake && (
    <GhostButton
      size="small"
      variant="main"
      className={baseStyles['button']}
      onClick={giveCakeBadge}
      biData={BiData<BuyCoreButtonBiEvent>({
        evid: 375,
        referral_info: ReferralType.DYW_BIRTHDAY,
      })}
    >
      {t(
        isMobile
          ? 'watch.birthday.banner.button.giftCake.mobile'
          : 'watch.birthday.banner.button.giftCake'
      )}
    </GhostButton>
  );

  const giftCoreButton = (
    <BrandedButton
      size="small"
      variant="core-orange"
      className={baseStyles['button']}
      href={Url.giveCoreMembership(user.username)}
      biData={BiData<BuyCoreButtonBiEvent>({
        evid: 375,
        referral_info: ReferralType.DYW_BIRTHDAY,
      })}
    >
      {t(
        isMobile
          ? 'watch.birthday.banner.button.upsell.mobile'
          : 'watch.birthday.banner.button.upsell'
      )}
    </BrandedButton>
  );

  return (
    <DialogBanner
      className={classnames(
        baseStyles['root'],
        baseStyles['gift-core'],
        className
      )}
      message={
        <span className={baseStyles['message']}>
          <BirthdayCake
            disableDefaultColors
            className={baseStyles['cake-icon']}
            role="presentation"
          />

          <span>
            {t(saleKey, {
              postProcess: ['reactPostprocessor', 'featureBrancher'],
              username: (
                <a
                  className="text-link-level-2-primary"
                  href={Url.userLink(username)}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {username}
                </a>
              ),
            })}
          </span>
        </span>
      }
      customCtaButton={
        showGiftCake ? (
          <Flex className={baseStyles['button-container']}>
            {giftCakeButton}
            {giftCoreButton}
          </Flex>
        ) : (
          giftCoreButton
        )
      }
    />
  );
};

UserBirthdayBanner.displayName = 'UserBirthdayBanner';

export default UserBirthdayBanner;
